import { AlertTriangle, ShieldCheck, ShieldAlert } from "lucide-react";

/**
 * Composite Site Health Score card — top-line 0-100 indicator combining
 * Coverage / Checkpoint / Response / Supervisor into a single "trust tier" number.
 *
 * Props:
 *   score       0-100 or null
 *   status      "green" | "yellow" | "red" | "unknown"
 *   breakdown   [{ name, status, weight, component_score }]
 *   weights     { coverage, checkpoint, response, supervisor }
 */
const STATUS_META = {
  green:   { color: "#22C55E", label: "STRONG",  Icon: ShieldCheck, ring: "#22C55E33" },
  yellow:  { color: "#EAB308", label: "WATCH",   Icon: AlertTriangle, ring: "#EAB30833" },
  red:     { color: "#EF4444", label: "AT RISK", Icon: ShieldAlert, ring: "#EF444433" },
  unknown: { color: "#6B7280", label: "PENDING", Icon: ShieldCheck, ring: "#6B728033" },
};

export default function CompositeHealthCard({ score, status, breakdown = [], weights = {} }) {
  const meta = STATUS_META[status] || STATUS_META.unknown;
  const Icon = meta.Icon;
  const displayScore = score == null ? "—" : Math.round(score);

  // SVG donut math
  const size = 128;
  const stroke = 12;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = score == null ? 0 : (Math.max(0, Math.min(100, score)) / 100) * circumference;

  return (
    <div
      data-testid="composite-health-card"
      className="border border-[#27272A] bg-[#121212] p-6 flex flex-col md:flex-row gap-6 items-center md:items-stretch"
      style={{ boxShadow: `inset 0 0 0 1px ${meta.ring}` }}
    >
      {/* Donut */}
      <div className="flex-shrink-0 relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#27272A"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={meta.color}
            strokeWidth={stroke}
            strokeDasharray={`${dash} ${circumference - dash}`}
            strokeDashoffset={circumference / 4}
            strokeLinecap="round"
            style={{ transition: "stroke-dasharray 400ms ease-out, stroke 300ms ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-mono font-black text-3xl tracking-tighter" style={{ color: meta.color }}>
            {displayScore}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">/ 100</div>
        </div>
      </div>

      {/* Copy + breakdown */}
      <div className="flex-1 flex flex-col justify-between gap-4 w-full min-w-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon className="size-4" style={{ color: meta.color }} strokeWidth={2.5} />
            <div className="label-mute">Site Health Score</div>
            <span
              className="px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold"
              style={{ color: meta.color, backgroundColor: meta.ring }}
              data-testid="composite-health-status"
            >
              {meta.label}
            </span>
          </div>
          <div className="font-display text-xl uppercase font-bold tracking-tight mt-2 text-zinc-100">
            {status === "green"   && "All trust metrics on target"}
            {status === "yellow"  && "One or more metrics need attention"}
            {status === "red"     && "Multiple metrics are underperforming"}
            {status === "unknown" && "Not enough data yet to compute"}
          </div>
          <div className="text-zinc-400 text-xs mt-1">
            Composite of Coverage / Checkpoint / Response / Supervisor — weighted by your account manager.
          </div>
        </div>

        {/* Component pills */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="composite-health-breakdown">
          {breakdown.map((b) => {
            const m = STATUS_META[b.status] || STATUS_META.unknown;
            return (
              <div
                key={b.name}
                className="border border-[#27272A] px-3 py-2 flex flex-col gap-1 bg-[#0A0A0A]"
                data-testid={`composite-health-component-${b.name}`}
              >
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center justify-between">
                  <span>{b.name}</span>
                  <span className="font-mono text-zinc-600">{Math.round(b.weight || 0)}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block size-2 rounded-full"
                    style={{ backgroundColor: m.color }}
                  />
                  <span className="text-xs font-mono uppercase tracking-widest" style={{ color: m.color }}>
                    {m.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
